// AI Rewrite Modal for Template Body:
// Calls whatsappRewriteTemplateBody to generate 3 high-converting variations
// with tone selection, strictly preserving {{n}} placeholders.

import { useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { rewriteTemplateBody } from '../../lib/whatsappApi';

export interface AiRewriteModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBody: string;
  language?: string;
  onApply: (newBody: string) => void;
}

type ToneOption = 'persuasive' | 'urgent' | 'friendly' | 'professional' | 'concise';

export default function AiRewriteModal({
  isOpen,
  onClose,
  currentBody,
  language,
  onApply,
}: AiRewriteModalProps) {
  const { t } = useI18n();

  const [tone, setTone] = useState<ToneOption>('persuasive');
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(isOpen, panelRef, onClose);

  if (!isOpen) return null;

  const tones: Array<{ id: ToneOption; label: string; icon: string }> = [
    { id: 'persuasive', label: t('tpl.ai.persuasive') || 'Persuasive', icon: '🎯' },
    { id: 'urgent', label: t('tpl.ai.urgent') || 'Urgent / FOMO', icon: '⚡' },
    { id: 'friendly', label: t('tpl.ai.friendly') || 'Friendly & Warm', icon: '😊' },
    { id: 'professional', label: t('tpl.ai.professional') || 'Professional', icon: '💼' },
    { id: 'concise', label: t('tpl.ai.concise') || 'Short & Punchy', icon: '✂️' },
  ];

  async function handleGenerate() {
    if (!currentBody.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await rewriteTemplateBody({
        body: currentBody.trim(),
        tone,
        language,
      });
      setVariants(res.variants || []);
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.includes('ai_not_configured')) {
        setError(
          t('tpl.ai.notConfigured') ||
            'AI key is not configured for this tenant. Please configure it under Settings > AI API.',
        );
      } else {
        setError(msg || 'Failed to rewrite body with AI');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-600 text-white font-bold text-sm">
              ✨
            </span>
            <div>
              <h3 id={titleId} className="text-base font-bold text-slate-900">
                {t('tpl.ai.title') || 'Rewrite Template with AI'}
              </h3>
              <p className="text-xs text-slate-500">
                {t('tpl.ai.subtitle') || 'Generate variations while keeping {{n}} placeholders intact'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-lg p-1 text-slate-400 hover:bg-white hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Tone Selector */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-2">
              {t('tpl.ai.selectTone') || 'Select Tone'}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {tones.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTone(item.id)}
                  className={`flex items-center gap-2 rounded-xl border p-2.5 text-xs font-medium transition-all ${
                    tone === item.id
                      ? 'border-indigo-600 bg-indigo-50/70 text-indigo-700 shadow-sm'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <span className="text-base">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Action Trigger */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading || !currentBody.trim()}
              className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>{t('tpl.ai.generating') || 'Generating 3 variants...'}</span>
                </>
              ) : (
                <>
                  <span>✨</span>
                  <span>{variants.length > 0 ? (t('tpl.ai.regenerate') || 'Regenerate') : (t('tpl.ai.generate') || 'Generate Variations')}</span>
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              ⚠️ {error}
            </div>
          )}

          {/* Variants Results */}
          {variants.length > 0 && (
            <div className="space-y-3 pt-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                {t('tpl.ai.chooseVariant') || 'Select a variation to apply:'}
              </h4>
              <div className="space-y-2.5">
                {variants.map((v, i) => (
                  <div
                    key={i}
                    className="group relative rounded-xl border border-slate-200 bg-slate-50/50 p-3.5 hover:border-indigo-400 hover:bg-white transition-all shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="whitespace-pre-wrap text-xs leading-relaxed text-slate-800 flex-1 font-normal">
                        {v}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          onApply(v);
                          onClose();
                        }}
                        className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
                      >
                        {t('tpl.ai.useThis') || 'Use This'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
